import { RoleEnum } from 'js/src/client';
import { getServerData } from 'js/src/libs/server-data';
import { getServiceLevel, hasFeatureEnabled, ServiceLevel } from 'js/src/libs/server-utils';
import type { DAMConfig } from 'js/src/typings/global';

const Flags = {
    canUseStaffControls: (): boolean => {
        return hasFeatureEnabled('staff_controls');
    },
    canSeeDamTab: (): boolean => {
        const damConfigs = getServerData()?.USER?.dam_configs;
        if (!damConfigs) {
            return false;
        }
        return damConfigs.some((damConfig: DAMConfig) => {
            if (damConfig.require_connection && damConfig.is_connected) {
                return true;
            }
            if (!damConfig.require_connection) {
                return true;
            }
            return false;
        });
    },
    canUseChat: () => {
        return hasFeatureEnabled('chat');
    },
    canUseToolbox: () => {
        return hasFeatureEnabled('toolbox');
    },
    chatTabDeveloperToolsEnabled: () => {
        return hasFeatureEnabled('sketch_show_dev_details');
    },
    canUseScreenRecording: () => {
        return getServerData()?.USER?.capabilities?.screen_recording ?? true;
    },
    canUseBlueprintAgent: () => {
        return hasFeatureEnabled('blueprint_agent');
    },
    canUseImproveMediaAgent: () => {
        return process.env.NODE_ENV !== 'test';
    },
    canUseAgentFamily: () => {
        if (getServiceLevel() === ServiceLevel.HIGH) {
            return Flags.canUseImproveMediaAgent() || Flags.canUseBlueprintAgent();
        } else {
            return false;
        }
    },
    canUseMediaAge: () => {
        return hasFeatureEnabled('enrich_media_age');
    },
    canUseMorphTransitions: () => {
        return hasFeatureEnabled('morph_transitions');
    },
    canUseAnimatedZoom: () => {
        return hasFeatureEnabled('animated_zoom') && Flags.canUseMorphTransitions();
    },
    canUseTransitionSFX: () => {
        return hasFeatureEnabled('sound_effects_transition');
    },
    canUseCodeo: () => {
        return hasFeatureEnabled('codeo');
    },
    canUseCodeoBrand: () => {
        return hasFeatureEnabled('codeo_brand');
    },
    disableGenxAutoImproveMedia: () => {
        return hasFeatureEnabled('disable_genx_auto_improve_media');
    },
    canUseVideoCreationApi: () => {
        return hasFeatureEnabled('video_creation_api_enabled');
    },
    canSeeAllTemplates: () => {
        return hasFeatureEnabled('view_all_templates');
    },
    canUseEnhancedAssetTransform: () => {
        return hasFeatureEnabled('enhanced_asset_transform');
    },
    canUseDisclaimerText: () => {
        return hasFeatureEnabled('disclaimer_text');
    },
    // RSS feeds is deprecated and hidden from the dashboard by default. The
    // pages and API still work, so the flag can re-expose the nav entry for
    // anyone who still relies on it.
    canAccessRssFeeds: () => {
        const role = getServerData()?.USER?.role;
        const roleHasAccess = role === RoleEnum.ADMIN || role === RoleEnum.EDITOR;
        return roleHasAccess && hasFeatureEnabled('rss_feeds');
    },
};

export default Flags;
